import React, { useId } from "react";
import styled from "styled-components";
import { Density, controlHeight, controlPadding, useDensity } from "./base/density";
import { bodyType, captionType, colorTransition, iconSlot } from "./base/mixins";
import Icon from "./Icon/Icon";

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
	/**
	 * Sits *below* the field, 4px gap, 12px secondary. §8: "the label is under the
	 * field, not above it." This is the house pattern — do not add a label above.
	 */
	helperText?: React.ReactNode;
	error?: boolean;
	/** 24px, 16px from the right edge. Search, chevron, unit. */
	trailingIcon?: React.ReactNode;
	/**
	 * Shows a clear button once there is a value. Without `onClear` it reports an
	 * empty value through `onChange`, which is what a search field wants.
	 */
	clearable?: boolean;
	onClear?: () => void;
	leadingIcon?: React.ReactNode;
	density?: Density;
	className?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
	(
		{
			helperText,
			error = false,
			trailingIcon,
			leadingIcon,
			clearable = false,
			onClear,
			density,
			className,
			id,
			...rest
		},
		ref,
	) => {
		const generated = useId();
		const inputId = id ?? generated;
		const helperId = helperText ? `${inputId}-helper` : undefined;
		const resolved = useDensity(density);
		const showClear = clearable && Boolean(rest.value);

		const handleClear = () => {
			if (onClear) {
				onClear();
				return;
			}
			rest.onChange?.({
				target: { value: "" },
				currentTarget: { value: "" },
			} as React.ChangeEvent<HTMLInputElement>);
		};

		return (
			<Field className={className}>
				<Shell $height={controlHeight(resolved)} $pad={controlPadding(resolved)} $error={error}>
					{leadingIcon ? <span className="hds-input__icon">{leadingIcon}</span> : null}
					<input
						ref={ref}
						id={inputId}
						aria-invalid={error || undefined}
						aria-describedby={helperId}
						{...rest}
					/>
					{showClear ? (
						<ClearButton type="button" aria-label="Clear" onClick={handleClear}>
							<Icon name="CloseIcon" size="var(--size-icon-sm)" />
						</ClearButton>
					) : null}
					{trailingIcon ? <span className="hds-input__icon">{trailingIcon}</span> : null}
				</Shell>
				{helperText ? (
					<Helper id={helperId} $error={error}>
						{helperText}
					</Helper>
				) : null}
			</Field>
		);
	},
);

Input.displayName = "Input";

const Field = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

const Shell = styled.div<{ $height: string; $pad: string; $error: boolean }>`
	display: flex;
	align-items: center;
	gap: var(--space-2);
	width: 100%;
	height: ${({ $height }) => $height};
	padding: 0 ${({ $pad }) => $pad};
	border: var(--border-default);
	border-color: ${({ $error }) => ($error ? "var(--color-danger)" : "var(--color-border)")};
	border-radius: var(--radius-md);
	background: var(--color-surface);
	box-shadow: none;
	${colorTransition}

	&:hover {
		border-color: ${({ $error }) =>
			$error ? "var(--color-danger)" : "var(--color-border-strong)"};
	}

	/* §9: a field shows focus on its own border, not with a ring around it. */
	&:focus-within {
		border-color: ${({ $error }) => ($error ? "var(--color-danger)" : "var(--focus-border)")};
	}

	.hds-input__icon {
		${iconSlot}
		color: var(--color-text-secondary);
	}

	input {
		flex: 1;
		min-width: 0;
		height: 100%;
		border: none;
		outline: none;
		background: transparent;
		padding: 0;
		${bodyType}

		&::placeholder {
			color: var(--color-text-secondary);
		}
		&:disabled {
			cursor: not-allowed;
			color: var(--color-text-disabled);
		}
	}
`;

const ClearButton = styled.button`
	${iconSlot}
	/* The 24px slot stays as the tap target, but the glyph inside it drops to
	   --size-icon-sm: a clear button is subordinate to the value it clears, and
	   at the full 24px it read heavier than the text beside it.

	   A direct svg child is a legacy glyph (a new-set one sits inside its own
	   span - see IconButton), and filled legacy ink needs the smaller box to
	   look the same size. The size passed to Icon covers the span; this has to
	   be a rule because iconSlot's own svg width is a descendant selector and
	   outranks what the Icon sets on itself. */
	> svg {
		width: var(--size-icon-sm-legacy);
		height: var(--size-icon-sm-legacy);
	}

	padding: 0;
	border: none;
	background: transparent;
	color: var(--color-text-secondary);
	cursor: pointer;
	transition: color var(--transition-base);

	&:hover {
		color: var(--color-text);
	}
	&:focus-visible {
		outline: var(--focus-ring);
		outline-offset: var(--focus-ring-offset);
	}
`;

const Helper = styled.span<{ $error: boolean }>`
	margin-top: var(--space-1);
	${captionType}
	color: ${({ $error }) => ($error ? "var(--color-danger)" : "var(--color-text-secondary)")};
`;

export default Input;
